import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import StatusBadge from '../../../components/StatusBadge';
import Button from '../../../components/Button';
import Spinner from '../../../components/Spinner';
import Modal from '../../../components/Modal';
import Input from '../../../components/Input';
import Toast from '../../../components/Toast';
import TenantService from '../../../services/tenant.service';
import ProductBrandingService from '../../../services/product-branding.service';
import { usePermission } from '../../../hooks/usePermission';
import { Modules, RouteParamsTypes, AuthRoutes } from '../../../enums';
import ConfigTab from './ConfigTab';
import SubscriptionTab from './SubscriptionTab';
import TenantUserListPanel from '../../tenant-users/list/TenantUserListPanel';
import TenantHealthCard from '../../../components/TenantHealthCard';
import ImpersonationStartModal from '../../../components/ImpersonationStartModal';

type TabKey = 'overview' | 'configuration' | 'subscription' | 'users' | 'activity';

const PRIMARY_COLOUR_VAR = '--bs-primary';

const hexToRgbTriplet = (hex: string): string | null => {
  const match = /^#([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})([0-9A-Fa-f]{2})$/.exec(hex);
  if (!match) return null;
  const [, r, g, b] = match;
  return `${parseInt(r, 16)}, ${parseInt(g, 16)}, ${parseInt(b, 16)}`;
};

/** US-023 AC-1/AC-2: next allowed action(s) label per current status, mirrors the backend's ALLOWED_TRANSITIONS map. */
const NEXT_ACTIONS: Record<string, { toStatusCode: string; labelKey: string }[]> = {
  trial: [{ toStatusCode: 'active', labelKey: 'activate' }],
  active: [
    { toStatusCode: 'suspended', labelKey: 'suspend' },
    { toStatusCode: 'archived', labelKey: 'archive' },
  ],
  suspended: [{ toStatusCode: 'active', labelKey: 'reactivate' }],
};

const PROVISIONING_POLL_MS = 10_000;
const PROMOTION_POLL_MS = 5_000;
const EXPORT_POLL_MS = 10_000;

const TenantDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const canManageUsers = usePermission('user:invite');
  const canImpersonate = usePermission('impersonate:start');
  const canExport = usePermission('tenant:export');
  const canManageRetention = usePermission('tenant:retention-manage');
  const [showImpersonateModal, setShowImpersonateModal] = useState(false);

  const [tab, setTab] = useState<TabKey>('overview');
  const [tenant, setTenant] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState<any>(null);
  const [provisioningHistory, setProvisioningHistory] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [promotion, setPromotion] = useState<any>(null);
  const [mandatoryCheck, setMandatoryCheck] = useState<{ complete: boolean; missingCount: number } | null>(null);

  const [transitionTarget, setTransitionTarget] = useState<{ toStatusCode: string; labelKey: string } | null>(null);
  const [transitionReason, setTransitionReason] = useState('');
  const [transitionSubmitting, setTransitionSubmitting] = useState(false);

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dependents, setDependents] = useState<{ dependents: string[]; deleteType: 'hard' | 'soft' } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const [retrying, setRetrying] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const [exportRecord, setExportRecord] = useState<any>(null);
  const [triggeringExport, setTriggeringExport] = useState(false);
  const [downloadingExport, setDownloadingExport] = useState(false);

  const [retainUntil, setRetainUntil] = useState('');
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [retentionSubmitting, setRetentionSubmitting] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const promotionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const exportPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const loadTenant = useCallback(() => {
    if (!id) return;
    setLoading(true);
    TenantService.get(id)
      .then((response: any) => setTenant(response?.data ?? null))
      .finally(() => setLoading(false));
  }, [id]);

  const loadProvisioning = useCallback(() => {
    if (!id) return;
    TenantService.getProvisioningStatus(id).then((response: any) => setProvisioning(response?.data ?? null));
  }, [id]);

  const loadPromotion = useCallback(() => {
    if (!id) return;
    TenantService.getIsolationPromotionCurrent(id).then((response: any) => setPromotion(response?.data ?? null));
  }, [id]);

  /** US-029 ST-003/ST-004: drives the Activate button's disabled/tooltip state; re-run after every config save. */
  const loadMandatoryCheck = useCallback(() => {
    if (!id) return;
    TenantService.getMandatoryCheck(id).then((response: any) => setMandatoryCheck(response?.data ?? null));
  }, [id]);

  /** US-048 AC-4/AC-5: latest export record for the status strip and Download button. */
  const loadExportStatus = useCallback(() => {
    if (!id || !canExport) return;
    TenantService.getExportStatus(id).then((response: any) => setExportRecord(response?.data ?? null));
  }, [id, canExport]);

  useEffect(() => {
    loadTenant();
    loadProvisioning();
    loadPromotion();
    loadMandatoryCheck();
    loadExportStatus();
  }, [loadTenant, loadProvisioning, loadPromotion, loadMandatoryCheck, loadExportStatus]);

  // US-056 ST-004: while viewing this tenant, apply its branding override (if one exists —
  // resolution falls back to product/global otherwise) to the topbar; restore the page's
  // original colour on unmount so leaving Tenant Detail doesn't leak the override elsewhere.
  useEffect(() => {
    if (!tenant?.id || !tenant?.product_id) return undefined;

    const root = document.documentElement;
    const previous = {
      colour: getComputedStyle(root).getPropertyValue(PRIMARY_COLOUR_VAR).trim(),
      rgb: getComputedStyle(root).getPropertyValue(`${PRIMARY_COLOUR_VAR}-rgb`).trim(),
    };

    let cancelled = false;
    ProductBrandingService.getForProduct(tenant.product_id, tenant.id)
      .then((response: any) => {
        if (cancelled) return;
        const resolved = response?.data;
        if (!resolved?.themeColour) return;
        root.style.setProperty(PRIMARY_COLOUR_VAR, resolved.themeColour);
        const rgb = hexToRgbTriplet(resolved.themeColour);
        if (rgb) root.style.setProperty(`${PRIMARY_COLOUR_VAR}-rgb`, rgb);
      })
      .catch(() => {
        // branding is a visual enhancement only — leave whatever colour is already applied
      });

    return () => {
      cancelled = true;
      root.style.setProperty(PRIMARY_COLOUR_VAR, previous.colour);
      root.style.setProperty(`${PRIMARY_COLOUR_VAR}-rgb`, previous.rgb);
    };
  }, [tenant?.id, tenant?.product_id]);

  // US-027 AC-6: poll provisioning status every 10s while Queued/Running
  useEffect(() => {
    const isActive = provisioning && ['queued', 'running'].includes(provisioning.status);
    if (isActive && !pollRef.current) {
      pollRef.current = setInterval(() => {
        loadProvisioning();
        loadTenant();
      }, PROVISIONING_POLL_MS);
    }
    if (!isActive && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [provisioning, loadProvisioning, loadTenant]);

  // US-057: poll isolation promotion every 5s while in an active stage
  useEffect(() => {
    const activeStages = ['queued', 'eligibility_check', 'limited_mode', 'data_migration', 'verification', 'cutover'];
    const isActive = promotion && activeStages.includes(promotion.status);
    if (isActive && !promotionPollRef.current) {
      promotionPollRef.current = setInterval(() => {
        loadPromotion();
        loadTenant();
      }, PROMOTION_POLL_MS);
    }
    if (!isActive && promotionPollRef.current) {
      clearInterval(promotionPollRef.current);
      promotionPollRef.current = null;
    }
    return () => {
      if (promotionPollRef.current) {
        clearInterval(promotionPollRef.current);
        promotionPollRef.current = null;
      }
    };
  }, [promotion, loadPromotion, loadTenant]);

  // US-048 AC-4: poll export status every 10s while Queued/Running
  useEffect(() => {
    const isActive = exportRecord && ['queued', 'running'].includes(exportRecord.status);
    if (isActive && !exportPollRef.current) {
      exportPollRef.current = setInterval(() => {
        loadExportStatus();
      }, EXPORT_POLL_MS);
    }
    if (!isActive && exportPollRef.current) {
      clearInterval(exportPollRef.current);
      exportPollRef.current = null;
    }
    return () => {
      if (exportPollRef.current) {
        clearInterval(exportPollRef.current);
        exportPollRef.current = null;
      }
    };
  }, [exportRecord, loadExportStatus]);

  const handleTriggerExport = useCallback(async () => {
    if (!id) return;
    setTriggeringExport(true);
    try {
      await TenantService.triggerExport(id);
      toast.success(<Toast message={t('tenants.detail.pageTexts.export.started')} />, { icon: false });
      loadExportStatus();
    } finally {
      setTriggeringExport(false);
    }
  }, [id, loadExportStatus, t]);

  const handleDownloadExport = useCallback(async () => {
    if (!id) return;
    setDownloadingExport(true);
    try {
      const response: any = await TenantService.downloadExport(id);
      const blob = response?.data instanceof Blob ? response.data : new Blob([response?.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tenant-${id}-export.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setDownloadingExport(false);
    }
  }, [id]);

  const handleSetRetentionOverride = useCallback(async () => {
    if (!id || !retainUntil) return;
    setRetentionSubmitting(true);
    try {
      await TenantService.setRetentionOverride(id, retainUntil);
      toast.success(<Toast message={t('tenants.detail.pageTexts.retention.overrideSuccess')} />, { icon: false });
      setShowRetentionModal(false);
      setRetainUntil('');
      loadTenant();
    } finally {
      setRetentionSubmitting(false);
    }
  }, [id, retainUntil, loadTenant, t]);

  const loadTimeline = useCallback(() => {
    if (!id) return;
    setTimelineLoading(true);
    TenantService.getTimeline(id, 1, 20)
      .then((response: any) => setTimeline(response?.data?.items ?? []))
      .finally(() => setTimelineLoading(false));
  }, [id]);

  const loadProvisioningHistory = useCallback(() => {
    if (!id) return;
    TenantService.getProvisioningHistory(id).then((response: any) => setProvisioningHistory(response?.data?.items ?? []));
  }, [id]);

  useEffect(() => {
    if (tab === 'activity') loadTimeline();
    if (tab === 'overview') loadProvisioningHistory();
  }, [tab, loadTimeline, loadProvisioningHistory]);

  const handleTabChange = useCallback((_e: any, value: TabKey) => setTab(value), []);

  const nextActions = useMemo(() => (tenant?.status_code ? NEXT_ACTIONS[tenant.status_code] ?? [] : []), [tenant]);

  const handleConfirmTransition = useCallback(async () => {
    if (!transitionTarget || !id) return;
    setTransitionSubmitting(true);
    try {
      await TenantService.transitionStatus(id, transitionTarget.toStatusCode, transitionReason);
      toast.success(<Toast message={t('tenants.detail.pageTexts.statusTransition.success')} />, { icon: false });
      setTransitionTarget(null);
      setTransitionReason('');
      loadTenant();
    } finally {
      setTransitionSubmitting(false);
    }
  }, [transitionTarget, transitionReason, id, loadTenant, t]);

  const openDeleteDialog = useCallback(async () => {
    if (!id) return;
    const response: any = await TenantService.checkDependents(id);
    setDependents(response?.data ?? null);
    setShowDeleteDialog(true);
  }, [id]);

  const handleConfirmDelete = useCallback(async () => {
    if (!id) return;
    setDeleteSubmitting(true);
    try {
      await TenantService.remove(id, deleteReason);
      toast.success(<Toast message={t('tenants.detail.pageTexts.delete.success')} />, { icon: false });
      navigate(`${Modules.tenants}${RouteParamsTypes.blank}`);
    } finally {
      setDeleteSubmitting(false);
      setShowDeleteDialog(false);
    }
  }, [id, deleteReason, navigate, t]);

  const handleRetryProvisioning = useCallback(async () => {
    if (!id) return;
    setRetrying(true);
    try {
      await TenantService.retryProvisioning(id);
      toast.success(<Toast message={t('tenants.detail.pageTexts.provisioning.retrySuccess')} />, { icon: false });
      loadProvisioning();
      loadTenant();
    } finally {
      setRetrying(false);
    }
  }, [id, loadProvisioning, loadTenant, t]);

  const handleStartPromotion = useCallback(async () => {
    if (!id) return;
    setPromoting(true);
    try {
      await TenantService.startIsolationPromotion(id);
      toast.success(<Toast message={t('tenants.detail.pageTexts.isolationPromotion.started')} />, { icon: false });
      loadPromotion();
    } catch (err) {
      // AppError message surfaced via HttpInterceptor toast
    } finally {
      setPromoting(false);
    }
  }, [id, loadPromotion, t]);

  const handleCancelPromotion = useCallback(async () => {
    if (!id) return;
    await TenantService.cancelIsolationPromotion(id);
    toast.success(<Toast message={t('tenants.detail.pageTexts.isolationPromotion.cancelled')} />, { icon: false });
    loadPromotion();
  }, [id, loadPromotion, t]);

  if (loading || !tenant) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  const canPromote = tenant.isolation_mode === 'shared' && tenant.status_code === 'active' && !promotion?.status;
  const promotionActive = promotion && !['complete', 'failed', 'rolled_back', 'cancelled'].includes(promotion.status);
  const promotionCancellable = promotion && ['limited_mode', 'data_migration'].includes(promotion.status);

  return (
    <>
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">{t('tenants.detail.pageTexts.breadcrumb.home')}</li>
          <li className="breadcrumb-item">{tenant.product_name ?? '—'}</li>
          <li className="breadcrumb-item">
            <a href={`${Modules.tenants}${RouteParamsTypes.blank}`} onClick={(e) => { e.preventDefault(); navigate(`${Modules.tenants}${RouteParamsTypes.blank}`); }}>
              {t('tenants.detail.pageTexts.breadcrumb.tenants')}
            </a>
          </li>
          <li className="breadcrumb-item active">{tenant.tenant_name}</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <h4 className="m-0">{tenant.tenant_name}</h4>
          <span className="text-muted">{tenant.tenant_code}</span>
          {tenant.status_name && tenant.status_colour && (
            <StatusBadge label={tenant.status_name} colour={tenant.status_colour} />
          )}
          <span className="badge bg-label-secondary">
            {t(`tenants.list.pageTexts.isolationModes.${tenant.isolation_mode}`)}
          </span>
        </div>
        <div className="d-flex gap-2">
          {nextActions.map((action) => {
            const isActivate = action.labelKey === 'activate';
            const blocked = isActivate && mandatoryCheck && !mandatoryCheck.complete;
            const button = (
              <Button
                key={action.toStatusCode}
                className="btn btn-outline-primary"
                text={t(`tenants.detail.pageTexts.statusTransition.actions.${action.labelKey}`)}
                disabled={!!blocked}
                onClick={() => setTransitionTarget(action)}
              />
            );
            if (!blocked) return button;
            return (
              <Tooltip
                key={action.toStatusCode}
                title={t('tenants.detail.pageTexts.configuration.activateBlockedTooltip', {
                  count: mandatoryCheck?.missingCount ?? 0,
                })}
              >
                <span>{button}</span>
              </Tooltip>
            );
          })}
          {tenant.status_code === 'archived' && (
            <Button
              className="btn btn-danger"
              text={t('tenants.detail.pageTexts.delete.action')}
              onClick={openDeleteDialog}
            />
          )}
          {tenant.status_code === 'deleted' && canManageRetention && (
            <Button
              className="btn btn-outline-warning"
              text={t('tenants.detail.pageTexts.retention.extendAction')}
              onClick={() => setShowRetentionModal(true)}
            />
          )}
          <Button
            className="btn btn-outline-secondary"
            text={t('tenants.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.tenants}${RouteParamsTypes.edit}/${id}`)}
          />
          {canImpersonate && (
            <Button
              className="btn btn-outline-danger"
              text="Impersonate"
              onClick={() => setShowImpersonateModal(true)}
            />
          )}
        </div>
      </div>

      {id && <TenantHealthCard tenantId={id} onConfigIssuesClick={() => setTab('configuration')} />}

      <div className="card mb-6">
        <MuiTabs value={tab} onChange={handleTabChange} textColor="primary">
          <Tab value="overview" label={t('tenants.detail.pageTexts.tabs.overview')} />
          <Tab value="configuration" label={t('tenants.detail.pageTexts.tabs.configuration')} />
          <Tab value="subscription" label={t('tenants.detail.pageTexts.tabs.subscription')} />
          <Tab value="users" label={t('tenants.detail.pageTexts.tabs.users')} />
          <Tab value="activity" label={t('tenants.detail.pageTexts.tabs.activity')} />
        </MuiTabs>
        <hr className="m-0" />

        <div className="card-body">
          {tab === 'overview' && (
            <div className="row g-4">
              {provisioning && (
                <div className="col-12">
                  <div className="alert alert-secondary d-flex justify-content-between align-items-center">
                    <span>
                      {t('tenants.detail.pageTexts.provisioning.statusLabel')}: <strong>{provisioning.status}</strong>
                      {provisioning.error_message && ` — ${provisioning.error_message}`}
                    </span>
                    {provisioning.status === 'failed' && (
                      <Button
                        className="btn btn-sm btn-warning"
                        text={t('tenants.detail.pageTexts.provisioning.retry')}
                        loading={retrying}
                        onClick={handleRetryProvisioning}
                      />
                    )}
                  </div>
                </div>
              )}

              {promotion && (
                <div className="col-12">
                  <div className="alert alert-info d-flex justify-content-between align-items-center">
                    <span>
                      {t('tenants.detail.pageTexts.isolationPromotion.statusLabel')}: <strong>{promotion.status}</strong>
                      {promotion.status === 'data_migration' && ` — ${promotion.progress_pct}%`}
                      {promotion.failure_reason && ` — ${promotion.failure_reason}`}
                    </span>
                    {promotionCancellable && (
                      <Button
                        className="btn btn-sm btn-outline-danger"
                        text={t('tenants.detail.pageTexts.isolationPromotion.cancel')}
                        onClick={handleCancelPromotion}
                      />
                    )}
                  </div>
                </div>
              )}

              {canExport && (
                <div className="col-12">
                  <div className="alert alert-light border d-flex justify-content-between align-items-center">
                    <span>
                      {t('tenants.detail.pageTexts.export.title')}
                      {exportRecord && (
                        <>
                          {' — '}
                          <strong>{exportRecord.status}</strong>
                          {exportRecord.status === 'failed' && exportRecord.error_message && ` — ${exportRecord.error_message}`}
                        </>
                      )}
                    </span>
                    <div className="d-flex gap-2">
                      {exportRecord?.status === 'ready' && (
                        <Button
                          className="btn btn-sm btn-primary"
                          text={t('tenants.detail.pageTexts.export.download')}
                          loading={downloadingExport}
                          onClick={handleDownloadExport}
                        />
                      )}
                      <Button
                        className="btn btn-sm btn-outline-primary"
                        text={
                          exportRecord?.status === 'failed'
                            ? t('tenants.detail.pageTexts.export.retry')
                            : t('tenants.detail.pageTexts.export.trigger')
                        }
                        loading={triggeringExport}
                        disabled={exportRecord && ['queued', 'running'].includes(exportRecord.status)}
                        onClick={handleTriggerExport}
                      />
                    </div>
                  </div>
                </div>
              )}

              {canPromote && !promotionActive && (
                <div className="col-12">
                  <Button
                    className="btn btn-outline-primary"
                    text={t('tenants.detail.pageTexts.isolationPromotion.promote')}
                    loading={promoting}
                    onClick={handleStartPromotion}
                  />
                </div>
              )}

              <OverviewField label={t('tenants.form.pageTexts.fields.tenantCode.label')} value={tenant.tenant_code} />
              <OverviewField label={t('tenants.form.pageTexts.fields.tenantName.label')} value={tenant.tenant_name} />
              <OverviewField label={t('tenants.form.pageTexts.fields.slug.label')} value={tenant.slug} />
              <OverviewField label={t('tenants.form.pageTexts.fields.product.label')} value={tenant.product_name} />
              <OverviewField label={t('tenants.form.pageTexts.fields.ownerName.label')} value={tenant.owner_name} />
              <OverviewField label={t('tenants.form.pageTexts.fields.ownerEmail.label')} value={tenant.owner_email} />
              <OverviewField label={t('tenants.form.pageTexts.fields.ownerPhone.label')} value={tenant.owner_phone ?? '—'} />
              <OverviewField label={t('tenants.form.pageTexts.fields.country.label')} value={tenant.country_name} />
              <OverviewField label={t('tenants.form.pageTexts.fields.timezone.label')} value={tenant.tz_name ?? '—'} />
              <OverviewField label={t('tenants.form.pageTexts.fields.category.label')} value={tenant.category_name ?? '—'} />
              <OverviewField label={t('tenants.form.pageTexts.fields.plan.label')} value={tenant.plan_name} />
              <OverviewField label={t('tenants.form.pageTexts.fields.startDate.label')} value={String(tenant.subscription_start_date).slice(0, 10)} />
              <OverviewField label={t('tenants.form.pageTexts.fields.addressNotes.label')} value={tenant.address_notes ?? '—'} />

              {provisioningHistory.length > 0 && (
                <div className="col-12">
                  <h6>{t('tenants.detail.pageTexts.provisioning.historyTitle')}</h6>
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{t('tenants.detail.pageTexts.provisioning.columns.attempt')}</th>
                        <th>{t('tenants.detail.pageTexts.provisioning.columns.status')}</th>
                        <th>{t('tenants.detail.pageTexts.provisioning.columns.started')}</th>
                        <th>{t('tenants.detail.pageTexts.provisioning.columns.completed')}</th>
                        <th>{t('tenants.detail.pageTexts.provisioning.columns.error')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {provisioningHistory.map((job) => (
                        <tr key={job.id}>
                          <td>{job.attempt_number}</td>
                          <td>{job.status}</td>
                          <td>{job.started_at ? new Date(job.started_at).toLocaleString() : '—'}</td>
                          <td>{job.completed_at ? new Date(job.completed_at).toLocaleString() : '—'}</td>
                          <td>{job.error_message ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'configuration' && id && (
            <ConfigTab tenantId={id} onValueSaved={loadMandatoryCheck} />
          )}

          {tab === 'subscription' && id && <SubscriptionTab tenantId={id} />}

          {tab === 'users' && id && (
            <div>
              {canManageUsers && (
                <div className="d-flex justify-content-end mb-3">
                  <Button
                    className="btn btn-primary"
                    text={t('tenants.detail.pageTexts.users.inviteUser')}
                    onClick={() => navigate(`${Modules.iam}${AuthRoutes.tenantUsers}${RouteParamsTypes.add}?tenantId=${id}`)}
                  />
                </div>
              )}
              <TenantUserListPanel tenantId={id} showTenantColumn={false} />
            </div>
          )}

          {tab === 'activity' && (
            <div>
              {timelineLoading ? (
                <Spinner />
              ) : (
                <ul className="list-unstyled">
                  {timeline.map((entry) => (
                    <li key={entry.id} className="mb-3 pb-3 border-bottom">
                      <div className="d-flex justify-content-between">
                        <strong>{entry.action_type}</strong>
                        <span className="text-muted small">{new Date(entry.created_at).toLocaleString()}</span>
                      </div>
                      <div>{entry.action_description}</div>
                      {entry.actor_name && <div className="text-muted small">{t('tenants.detail.pageTexts.activity.by')} {entry.actor_name}</div>}
                    </li>
                  ))}
                  {timeline.length === 0 && <div className="text-muted">{t('tenants.detail.pageTexts.activity.empty')}</div>}
                </ul>
              )}
              <a
                href={`${Modules.home}${AuthRoutes.auditLog}?tenantId=${id}`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`${Modules.home}${AuthRoutes.auditLog}?tenantId=${id}`);
                }}
                className="small"
              >
                {t('tenants.detail.pageTexts.activity.viewAllInAuditLog')}
              </a>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={!!transitionTarget}
        onClose={() => setTransitionTarget(null)}
        title={transitionTarget ? t(`tenants.detail.pageTexts.statusTransition.actions.${transitionTarget.labelKey}`) : ''}
        content={
          <>
            <p>{transitionTarget ? t(`tenants.detail.pageTexts.statusTransition.impact.${transitionTarget.labelKey}`) : ''}</p>
            <Input
              id="transitionReason"
              name="transitionReason"
              label={t('tenants.detail.pageTexts.statusTransition.reasonLabel')}
              required
              value={transitionReason}
              onChange={(e) => setTransitionReason(e.target.value)}
            />
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('tenants.form.pageTexts.buttons.cancel')} onClick={() => setTransitionTarget(null)} />
            <Button
              className="btn btn-primary"
              text={t('tenants.detail.pageTexts.statusTransition.confirm')}
              loading={transitionSubmitting}
              disabled={!transitionReason.trim()}
              onClick={handleConfirmTransition}
            />
          </>
        }
      />

      <Modal
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        title={t('tenants.detail.pageTexts.delete.title')}
        content={
          <>
            <p>
              {dependents?.deleteType === 'hard'
                ? t('tenants.detail.pageTexts.delete.hardMessage')
                : t('tenants.detail.pageTexts.delete.softMessage', { types: dependents?.dependents.join(', ') })}
            </p>
            <Input
              id="deleteReason"
              name="deleteReason"
              label={t('tenants.detail.pageTexts.delete.reasonLabel')}
              required
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
            />
          </>
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('tenants.form.pageTexts.buttons.cancel')} onClick={() => setShowDeleteDialog(false)} />
            <Button
              className="btn btn-danger"
              text={t('tenants.detail.pageTexts.delete.confirm')}
              loading={deleteSubmitting}
              disabled={!deleteReason.trim()}
              onClick={handleConfirmDelete}
            />
          </>
        }
      />

      <Modal
        open={showRetentionModal}
        onClose={() => setShowRetentionModal(false)}
        title={t('tenants.detail.pageTexts.retention.modalTitle')}
        content={
          <Input
            id="retainUntil"
            name="retainUntil"
            type="date"
            label={t('tenants.detail.pageTexts.retention.retainUntilLabel')}
            required
            value={retainUntil}
            onChange={(e) => setRetainUntil(e.target.value)}
          />
        }
        action={
          <>
            <Button className="btn btn-outline-secondary" text={t('tenants.form.pageTexts.buttons.cancel')} onClick={() => setShowRetentionModal(false)} />
            <Button
              className="btn btn-primary"
              text={t('tenants.detail.pageTexts.retention.confirm')}
              loading={retentionSubmitting}
              disabled={!retainUntil}
              onClick={handleSetRetentionOverride}
            />
          </>
        }
      />

      {id && (
        <ImpersonationStartModal
          open={showImpersonateModal}
          tenantId={Number(id)}
          tenantName={tenant.tenant_name}
          onClose={() => setShowImpersonateModal(false)}
        />
      )}
    </>
  );
};

const OverviewField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="col-md-3">
    <label className="form-label d-block text-muted mb-1">{label}</label>
    <div>{value}</div>
  </div>
);

export default TenantDetail;
